// ── Enums ───────────────────────────────────────────────────────────────────────

export type WorkflowStatus =
  | 'Draft'
  | 'Submitted'
  | 'PendingApproval'
  | 'PartiallyApproved'
  | 'Approved'
  | 'Rejected'
  | 'Cancelled'
  | 'Escalated'
  | 'Posted';

export type PostingStatus = 'NotPosted' | 'Posted' | 'Failed' | 'Cancelled';

// ── DTOs (mirror backend GlJournalDto) ─────────────────────────────────────────

export interface GlJournalLineDto {
  id:            string;
  journalId:     string;
  lineNumber:    number;
  accountId:     string;
  accountCode:   string;
  accountName:   string;
  costCenterId?: string | null;
  costCenterCode?: string | null;
  costCenterName?: string | null;
  locationId?:   string | null;
  locationCode?: string | null;
  locationName?: string | null;
  currencyCode:  string;
  exchangeRate:  number;
  foreignDebit:  number;
  foreignCredit: number;
  debitAmount:   number;
  creditAmount:  number;
  narration?:    string | null;
  reference?:    string | null;
  taxCode?:      string | null;
}

export interface GlJournalWorkflowHistoryDto {
  id:           string;
  action:       string;
  fromStatus:   string;
  toStatus:     string;
  remarks?:     string | null;
  actionBy:     string;
  actionByName: string;
  actionDate:   string;
}

export interface GlJournalDto {
  id:                  string;
  companyId:           string;
  locationId?:         string | null;
  locationName?:       string | null;
  costCenterId?:       string | null;
  costCenterName?:     string | null;
  financialYearCode:   string;
  financialPeriodCode: string;
  transactionTypeCode: string;
  documentNumber:      string;
  documentDate:        string;
  postingDate:         string;
  reference?:          string | null;
  narration?:          string | null;
  currencyCode:        string;
  exchangeRate:        number;
  workflowStatus:      WorkflowStatus;
  postingStatus:       PostingStatus;
  submittedBy?:        string | null;
  submittedByName?:    string | null;
  submittedDate?:      string | null;
  approvedBy?:         string | null;
  approvedByName?:     string | null;
  approvedDate?:       string | null;
  rejectedBy?:         string | null;
  rejectedByName?:     string | null;
  rejectedDate?:       string | null;
  rejectionReason?:    string | null;
  postedBy?:           string | null;
  postedByName?:       string | null;
  postedDate?:         string | null;
  cancellationReason?: string | null;
  isReversal:          boolean;
  originalJournalId?:  string | null;
  reversalJournalId?:  string | null;
  totalDebit:          number;
  totalCredit:         number;
  totalFxDebit:        number;
  totalFxCredit:       number;
  isBalanced:          boolean;
  createdBy?:          string | null;
  createdByName?:      string | null;
  createdDate:         string;
  modifiedBy?:         string | null;
  modifiedDate?:       string | null;
  lines:               GlJournalLineDto[];
  workflowHistory:     GlJournalWorkflowHistoryDto[];
}

export interface GlJournalListDto {
  id:                  string;
  documentNumber:      string;
  documentDate:        string;
  postingDate:         string;
  transactionTypeCode: string;
  currencyCode:        string;
  totalDebit:          number;
  totalCredit:         number;
  workflowStatus:      WorkflowStatus;
  postingStatus:       PostingStatus;
  narration?:          string | null;
  reference?:          string | null;
  createdDate:         string;
  createdByName?:      string | null;
}

// ── Filter / Query ─────────────────────────────────────────────────────────────

export interface GlJournalFilterDto {
  companyId?:           string;
  locationId?:          string;   // session-context default; omit to use backend enforcement
  costCenterId?:        string;   // session-context default; omit to use backend enforcement
  transactionTypeCode?: string;
  documentNumber?:      string;
  workflowStatus?:      string;
  postingStatus?:       string;
  financialYearCode?:   string;   // session-context default; omit to use backend enforcement
  financialPeriodCode?: string;
  dateFrom?:            string;
  dateTo?:              string;
  reference?:           string;
  narration?:           string;
  pageNumber?:          number;
  pageSize?:            number;
  sortBy?:              string;
  sortDescending?:      boolean;
}

// ── Request payloads ───────────────────────────────────────────────────────────

export interface GlJournalLineRequest {
  lineNumber:    number;
  accountId:     string;
  costCenterId?: string | null;
  locationId?:   string | null;
  currencyCode:  string;
  exchangeRate:  number;
  foreignDebit:  number;
  foreignCredit: number;
  debitAmount:   number;
  creditAmount:  number;
  narration?:    string | null;
  reference?:    string | null;
}

export interface CreateGlJournalRequest {
  locationId?:         string | null;
  locationCode?:       string | null;   // passed for document number generation
  costCenterId?:       string | null;
  transactionTypeCode: string;
  documentDate:        string;
  postingDate:         string;
  reference?:          string | null;
  narration?:          string | null;
  currencyCode:        string;
  exchangeRate:        number;
  lines:               GlJournalLineRequest[];
}

export interface UpdateGlJournalRequest {
  locationId?:   string | null;
  costCenterId?: string | null;
  documentDate:  string;
  postingDate:   string;
  reference?:    string | null;
  narration?:    string | null;
  currencyCode:  string;
  exchangeRate:  number;
  lines:         GlJournalLineRequest[];
}

// ── Status helpers ─────────────────────────────────────────────────────────────

export const WORKFLOW_STATUS_LABELS: Record<WorkflowStatus, string> = {
  Draft:             'Draft',
  Submitted:         'Submitted',
  PendingApproval:   'Pending Approval',
  PartiallyApproved: 'Partially Approved',
  Approved:          'Approved',
  Rejected:          'Rejected',
  Cancelled:         'Cancelled',
  Escalated:         'Escalated',
  Posted:            'Posted',
};

export const WORKFLOW_STATUS_COLORS: Record<WorkflowStatus, string> = {
  Draft:             'bg-gray-100 text-gray-700',
  Submitted:         'bg-blue-100 text-blue-700',
  PendingApproval:   'bg-amber-100 text-amber-700',
  PartiallyApproved: 'bg-orange-100 text-orange-700',
  Approved:          'bg-green-100 text-green-700',
  Rejected:          'bg-red-100 text-red-700',
  Cancelled:         'bg-gray-200 text-gray-500',
  Escalated:         'bg-purple-100 text-purple-700',
  Posted:            'bg-emerald-100 text-emerald-700',
};
